import React, { useEffect, useState } from 'react';
import { referralChannelPrimaryContentOptions } from 'types';
import { ReactComponent as IdeaIcon } from 'assets/icons/idea.svg';
import {
  RadioButtonGroup,
  RadioButton,
  TextInput,
  Toggle
} from 'carbon-components-react';
import { isEmptyStr } from 'utilities/helper.util';
import { FormError } from 'components';
import { isEmpty } from 'lodash';

const PrimaryContentForm: React.FC<Props> = ({
  primaryContentOptions,
  setPrimaryContentInvalid,
  setPrimaryContentDirty
}) => {
  const [fileNameOption, setFileNameOption] = useState<string>(
    primaryContentOptions?.current?.filenameOption || ''
  );
  const [filename, setFileName] = useState<string>(
    primaryContentOptions?.current?.filenameValue || ''
  );
  const [fileNameToolTip, setFileNameToolTip] = useState('');
  const [isRequiredfilename, setIsRequiredfilename] = useState<boolean>(false);
  const [usedWebMap, setUsedWebMap] = useState<boolean>(true);
  const [responseType, setResponseType] = useState<string>('response_body');
  const [disabledFileNameOption, setDisabledFileNameOption] = useState<boolean>(
    true
  );

  useEffect(() => {
    let desc = '';
    switch (fileNameOption) {
      case 'start_with':
      case 'contains':
        desc = 'Case insensitive';
        break;
      case 'end_with':
        desc =
          'Case insentive. For this option, ensure you include the file type extension, for example .pdf';
        break;
      case 'reg_exp':
        desc = 'Case sensitive';
        break;
      default:
        desc = '';
        break;
    }

    setFileNameToolTip(desc);
  }, [fileNameOption]);

  const onChangeHandler = (val: string) => {
    setFileName('');
    setFileNameOption(val);
    setIsRequiredfilename(false);
    setReturnValue(usedWebMap, responseType, val, '');

    typeof setPrimaryContentDirty === 'function' &&
      setPrimaryContentDirty(true);
  };

  const handleBlur = (value: string) => {
    setFileName(value);
    setIsRequiredfilename(isEmptyStr(value));
    setReturnValue(usedWebMap, responseType, fileNameOption, value);

    typeof setPrimaryContentDirty === 'function' &&
      setPrimaryContentDirty(true);
  };

  const onFileanmeChangeHandler = (value: string) => {
    setFileName(value);
    setIsRequiredfilename(isEmptyStr(value));
    setReturnValue(usedWebMap, responseType, fileNameOption, value);

    typeof setPrimaryContentDirty === 'function' &&
      setPrimaryContentDirty(true);
  };

  const handleToggleActionButton = (toggle: boolean) => {
    setUsedWebMap(toggle);
    setReturnValue(toggle, responseType, fileNameOption, filename);

    typeof setPrimaryContentDirty === 'function' &&
      setPrimaryContentDirty(true);
  };

  const onResponseTypeChangeHandler = (val: string) => {
    setFileName('');
    setFileNameOption('');
    setIsRequiredfilename(false);
    setResponseType(val);
    setReturnValue(usedWebMap, val, '', '');
    setDisabledFileNameOption(val === 'response_body');

    typeof setPrimaryContentDirty === 'function' &&
      setPrimaryContentDirty(true);
  };

  const setReturnValue = (
    webMap: boolean,
    responseType: string,
    fileNameOption: string,
    filename: string
  ) => {
    if (primaryContentOptions?.current) {
      primaryContentOptions.current.filenameOption = fileNameOption;
      primaryContentOptions.current.filenameValue = filename;
      primaryContentOptions.current.responseType = responseType;
      primaryContentOptions.current.webMap = webMap;
    } else {
      primaryContentOptions.current = {
        filenameOption: fileNameOption,
        filenameValue: filename,
        responseType: responseType,
        webMap: webMap
      };
    }

    if (
      responseType !== 'response_body' &&
      (isEmpty(fileNameOption) ||
        (!isEmpty(fileNameOption) && isEmpty(filename)))
    ) {
      setIsRequiredfilename(true);
      setPrimaryContentInvalid(true);
    } else {
      setIsRequiredfilename(false);
      setPrimaryContentInvalid(false);
    }
  };

  useEffect(() => {
    if (primaryContentOptions?.current) {
      if (primaryContentOptions?.current?.filenameOption) {
        setFileNameOption(primaryContentOptions.current.filenameOption);
      }
      if (primaryContentOptions?.current?.filenameValue) {
        setFileName(primaryContentOptions.current.filenameValue);
      }
      setUsedWebMap(primaryContentOptions?.current?.webMap || false);
      if (primaryContentOptions?.current?.responseType) {
        setResponseType(primaryContentOptions.current.responseType);
        setDisabledFileNameOption(
          primaryContentOptions.current.responseType === 'response_body'
        );
      } else {
        if (
          primaryContentOptions?.current?.filenameOption &&
          primaryContentOptions?.current?.filenameValue
        ) {
          setResponseType('response_attachment');
          setDisabledFileNameOption(
            primaryContentOptions.current.responseType === 'response_body'
          );
        }
      }
    } else {
      primaryContentOptions.current = {
        filenameOption: fileNameOption,
        filenameValue: filename,
        responseType: responseType,
        webMap: usedWebMap
      };
    }
    // eslint-disable-next-line
  }, [primaryContentOptions, primaryContentOptions?.current]);

  return (
    <React.Fragment>
      <section className="primary_content">
        <h4 className="section__sub-heading">Hero attachment</h4>
        <p className="primary_content__section_text">
          Your hero attachment will appear as the default view for enquirers
          when they open your response. You can nominate the default content
          using one of the methods here. If there is no match, your response
          body text will be displayed by default, except where an automatic web
          map link is used.&nbsp;
          <a
            href="https://help.byda.com.au/article/configuring-hero-attachments/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more about hero attachment
          </a>
        </p>
        <p className="primary_content__section--toggle">
          <Toggle
            aria-label="Use web map (where available)"
            labelA="Use web map (where available)"
            labelB="Use web map (where available)"
            id="toggle-use-web-map"
            toggled={usedWebMap}
            defaultToggled={usedWebMap}
            onToggle={(toggle) => handleToggleActionButton(toggle)}
          />
          <p className="primary_content__label">
            If Use web map is turned off, a web map will not be used as hero
            attachment
          </p>
          <hr className="primary_content__section--divider" />
        </p>

        <p>
          <div className="primary_content__label">Response type</div>
          <RadioButtonGroup
            name="responseType"
            orientation="horizontal"
            valueSelected={responseType}
            onChange={(val: string) => {
              onResponseTypeChangeHandler(val);
            }}
          >
            <RadioButton
              id="responseType-1"
              labelText="Always use response body"
              value="response_body"
            />
            <RadioButton
              id="responseType-2"
              labelText="Always use response attachment"
              value="response_attachment"
            />
          </RadioButtonGroup>
          <div className="primary_content__label">
            If you select Always use response attachment and there is no
            filename match, the response body will be displayed instead, except
            where a web map link is available
          </div>
          <hr className="primary_content__section--divider" />
        </p>

        <p className="primary_content__label">Response attachment filename</p>
        <RadioButtonGroup
          name="filenameOptions"
          orientation="horizontal"
          valueSelected={fileNameOption}
          onChange={(val: string) => {
            onChangeHandler(val);
          }}
        >
          <RadioButton
            id="filenameOptions-1"
            labelText="Starts with"
            value="start_with"
            disabled={disabledFileNameOption}
          />
          <RadioButton
            id="filenameOptions-2"
            labelText="Ends with"
            value="end_with"
            disabled={disabledFileNameOption}
          />
          <RadioButton
            id="filenameOptions-3"
            labelText="Contains"
            value="contains"
            disabled={disabledFileNameOption}
          />
          <RadioButton
            id="filenameOptions-4"
            labelText="Regular expression"
            value="reg_exp"
            disabled={disabledFileNameOption}
          />
        </RadioButtonGroup>
        <div className="primary_content__input">
          <TextInput
            id="filename"
            name="filename"
            labelText=""
            placeholder={
              fileNameOption === 'reg_exp' ? 'example: ^\\d{8}\\.pdf' : 'text'
            }
            value={filename}
            onChange={(e) => onFileanmeChangeHandler(e.target.value)}
            onBlur={(evt) => handleBlur(evt.target.value)}
            disabled={isEmptyStr(fileNameOption)}
          />
        </div>
        <p className="footer--content">{fileNameToolTip}</p>
        {isRequiredfilename && (
          <FormError>{'This is a required field!'}</FormError>
        )}
        <div className="primary_content__section-flex">
          <IdeaIcon className="idea-icon" />
          <div className="primary_content__tip">
            Tip: the best hero attachment is usually the PDF that contains your
            plans
          </div>
        </div>
      </section>
      <hr className="section--divider" />
    </React.Fragment>
  );
};

type Props = {
  primaryContentOptions: React.MutableRefObject<
    referralChannelPrimaryContentOptions | undefined
  >;
  setPrimaryContentInvalid: Function;
  setPrimaryContentDirty?: Function;
};

export default React.memo(PrimaryContentForm);
